import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal, Signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { TuiSheetModule, TuiSheetOptions } from '@taiga-ui/addon-mobile';
import { TuiDestroyService, TuiLetModule } from '@taiga-ui/cdk';
import { TuiButtonModule, TuiLinkModule, TuiLoaderModule, TuiSvgModule } from '@taiga-ui/core';
import {
    Cart,
    CartActions,
    CartItem,
    CartPageStore,
    selectCart,
    selectCartDelivery,
    selectCartItems,
    selectCartPaymentType,
    selectIsCartShared,
    selectLoading,
    selectOrderType,
    selectShowAddItemButton
} from '@us/web/cart/data-access';
import { OrderItemsComponent, OrderShippingDetailsComponent } from '@us/web/orders/feature';
import { PaymentTypeOptionsComponent } from '@us/web/orders/ui';
import {
    BusinessModel,
    FormattedAddressPipe,
    NgForTrackByPropertyDirective,
    OrderDelivery,
    OrderItem,
    OrderType,
    PaymentType,
    PaymentTypeIconPipe,
    PaymentTypeLabelPipe
} from '@us/web/shared/data-access';
import { FormatCurrencyPipe, selectPaymentMethods } from '@us/web/shop/data-access';
import { CartComponent, QuantityChangedOutputType } from '../../components/cart/cart.component';
import { PromotionSelectComponent } from '@us/web/promotions/feature';
import { selectPersistedUserId } from '@us/web/auth/data-access';

@Component({
    selector: 'us-cart-delivery',
    standalone: true,
    templateUrl: './cart-delivery.component.html',
    styleUrls: ['./cart-delivery.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        IonicModule,
        TranslocoModule,
        TuiSvgModule,
        FormattedAddressPipe,
        CartComponent,
        OrderItemsComponent,
        TuiButtonModule,
        FormatCurrencyPipe,
        TuiSheetModule,
        TuiLetModule,
        PaymentTypeOptionsComponent,
        PaymentTypeIconPipe,
        PaymentTypeLabelPipe,
        TuiLoaderModule,
        RouterLink,
        OrderShippingDetailsComponent,
        PromotionSelectComponent,
        NgForTrackByPropertyDirective,
        TuiLinkModule
    ],
    providers: [TuiDestroyService]
})
export class CartDeliveryComponent {
    private readonly store = inject(Store);
    private readonly cartStore = inject(CartPageStore);

    private readonly currentUserId = this.store.selectSignal(selectPersistedUserId);
    readonly cart: Signal<Cart> = this.store.selectSignal(selectCart);
    readonly isCartOwner = this.cartStore.isCartOwner;
    readonly isCartShared = this.store.selectSignal(selectIsCartShared);
    readonly items = this.store.selectSignal(selectCartItems);
    readonly cartItems: Signal<OrderItem[]> = this.store.selectSignal(selectCartItems);
    readonly cartItemsLength: Signal<number> = computed(() => this.cartItems().length);
    readonly loading = this.store.selectSignal(selectLoading);
    readonly paymentMethods: Signal<PaymentType[]> = this.store.selectSignal(selectPaymentMethods);
    readonly selectedPaymentMethod: Signal<PaymentType | null> = this.store.selectSignal(selectCartPaymentType);
    readonly orderType: Signal<OrderType | null> = this.store.selectSignal(selectOrderType);
    readonly showAddItemButton = this.store.selectSignal(selectShowAddItemButton);

    protected readonly options: Partial<TuiSheetOptions> = {
        overlay: true
    };
    showPaymentMethodsSheet = signal(false);

    readonly delivery: Signal<OrderDelivery | null> = this.store.selectSignal(selectCartDelivery);

    readonly OrderType = OrderType;

    onChangeItemQuantity(event: QuantityChangedOutputType) {
        const { item, quantity } = event;

        if (quantity === 0) {
            this.onRemoveItemFromCart(item);
            return;
        }

        this.store.dispatch(CartActions.updateItemQuantity({ item, quantity }));
    }

    onRemoveItemFromCart(item: CartItem) {
        this.store.dispatch(CartActions.removeFromCart({ item }));
    }

    onChangePaymentType(paymentType: PaymentType) {
        this.store.dispatch(CartActions.updateCart({ payment_type: paymentType }));
        this.showPaymentMethodsSheet.set(false);
    }

    togglePaymentMethodsSheet() {
        this.showPaymentMethodsSheet.update(e => !e);
    }

    handleSubmitCart(): void {
        this.store.dispatch(CartActions.submitCart({ businessModel: BusinessModel.PrePayment }));
    }

    leaveGroupOrder() {
        this.store.dispatch(CartActions.removeCartMember({ userId: this.currentUserId() }));
    }
}
